import { t } from '@lingui/macro';
import cloneDeep from 'lodash/cloneDeep';
import React from 'react';
import certificatesActions from '../../../components/certificates/certificates-actions';
import CertificateDisplay from '../../../components/participation-table/certificate-display/certificate-display';
import { END_DATE, START_DATE } from '../../../helpers/constants';
import UrlHelper from '../../../helpers/url-helper';
import {
    BRONZE,
    DIAMOND,
    EMERALD,
    EPIC,
    GOLD,
    LEGEND,
    NEW_TOTAL,
    PAST_TOTAL,
    PLATINUM,
    SILVER
} from '../config/certificate-constants';
import css from './all-classes.scss';

export const CLASS_ID = 'id';
export const CLASS_NAME = 'classroomName';
export const ALL_CLASSES_ID = 0;
export const ALL_GROUPS_ID = 0;

export const NEW_CERTIFICATES_TOTAL = 'newCertificatesTotal';
export const PAST_CERTIFICATES_TOTAL = 'pastCertificatesTotal';
export const NEW_CERTIFICATES_BRONZE = 'newCertificatesBronze';
export const NEW_CERTIFICATES_SILVER = 'newCertificatesSilver';
export const NEW_CERTIFICATES_GOLD = 'newCertificatesGold';
export const NEW_CERTIFICATES_PLATINUM = 'newCertificatesPlatinum';
export const NEW_CERTIFICATES_EMERALD = 'newCertificatesEmerald';
export const NEW_CERTIFICATES_DIAMOND = 'newCertificatesDiamond';
export const NEW_CERTIFICATES_EPIC = 'newCertificatesEpic';
export const NEW_CERTIFICATES_LEGEND = 'newCertificatesLegend';
export const PAST_CERTIFICATES_BRONZE = 'bronze';
export const PAST_CERTIFICATES_SILVER = 'silver';
export const PAST_CERTIFICATES_GOLD = 'gold';
export const YEAR_GROUP = 'yearGroup';

const ACTIVITY_POINTS = 'activityPoints';
const LIVE_POINTS = 'livePoints';
const TOTAL_POINTS = 'totalPoints';
const SIGN_INS = 'totalSignIns';
const TIME_ONLINE = 'totalTimeOnline';
const TOTAL_ACTIVITY_POINTS = 'totalActivityPoints';
const TOTAL_SKILL_QUESTS_POINTS = 'totalSkillQuestsPoints';
const TOTAL_LIVE_POINTS = 'totalLivePoints';
const sort = { sortAs: 'number', sortDir: 'desc' };
const getCertificateName = (id, name) => {
    const i18n = window.i18n;
    switch (id) {
        case NEW_CERTIFICATES_TOTAL:
            return i18n._(t`Filename - New Certificates - ${name}`);
        case PAST_CERTIFICATES_TOTAL:
            return i18n._(t`Filename - Past Certificates - ${name}`);
        case NEW_CERTIFICATES_BRONZE:
        case PAST_CERTIFICATES_BRONZE:
            return i18n._(t`Filename - Bronze - ${name}`);
        case NEW_CERTIFICATES_SILVER:
        case PAST_CERTIFICATES_SILVER:
            return i18n._(t`Filename - Silver - ${name}`);
        case NEW_CERTIFICATES_GOLD:
        case PAST_CERTIFICATES_GOLD:
            return i18n._(t`Filename - Gold - ${name}`);
        case NEW_CERTIFICATES_EMERALD:
            return i18n._(t`Filename - Emerald - ${name}`);
        case NEW_CERTIFICATES_DIAMOND:
            return i18n._(t`Filename - Diamond - ${name}`);
        case NEW_CERTIFICATES_EPIC:
            return i18n._(t`Filename - Epic - ${name}`);
        case NEW_CERTIFICATES_LEGEND:
            return i18n._(t`Filename - Legend - ${name}`);
        case NEW_CERTIFICATES_PLATINUM:
            return i18n._(t`Filename - Platinum - ${name}`);
        default:
            return '';
    }
};

const getCertificateDisplayColumn = ({
    id,
    name,
    type,
    service,
    isAllYears,
    isTopTen,
    isOldCertificates = false
}) => ({
    id,
    name,
    transform: (value) => {
        const classroomId = isAllYears
            ? ALL_CLASSES_ID
            : value.classroomId || ALL_CLASSES_ID;
        const yearGroupEnum = isAllYears ? value.classroomId : null;
        const userProfileId = isTopTen ? value.id : 0;
        const requestParams = {
            classroomId,
            startDate: UrlHelper.getQueryParamValue(START_DATE) || null,
            endDate: UrlHelper.getQueryParamValue(END_DATE) || null,
            groupId: ALL_GROUPS_ID,
            isForDownload: true,
            type,
            ...(userProfileId && { userProfileId }),
            ...(yearGroupEnum !== null && { yearGroupEnum }) // yearGroupEnum = 0 is a valid value for kindergarten
        };
        const onSuccessParams = {
            certificateType: type,
            certificatesCount: value[id],
            fileNameOverride: getCertificateName(id, value.classroomName)
        };
        return (
            <CertificateDisplay
                count={value[id]}
                requestParams={requestParams}
                service={service}
                onSuccessParams={onSuccessParams}
                isOldCertificates={isOldCertificates}
            />
        );
    }
});

/**
 * New version of participation reports requires dynamic table config for new and past certificates.
 * Separating the constants files that manage these configs to promote a smooth transition between old and new.
 * Consider that reports will still have other areas that need to remain the same.
 */
const getAllClassesParticipationPastCertificates = (isExpanded, isAllYears) => {
    if (isExpanded) {
        const defaultParams = {
            service: certificatesActions.pastCertificatesDownloadByType,
            isAllYears,
            isOldCertificates: true
        };
        return {
            cols: [
                getCertificateDisplayColumn({
                    id: PAST_CERTIFICATES_BRONZE,
                    name: 'Certificates - Bronze',
                    type: BRONZE.type,
                    ...defaultParams
                }),
                getCertificateDisplayColumn({
                    id: PAST_CERTIFICATES_SILVER,
                    name: 'Certificates - Silver',
                    type: SILVER.type,
                    ...defaultParams
                }),
                getCertificateDisplayColumn({
                    id: PAST_CERTIFICATES_GOLD,
                    name: 'Certificates - Gold',
                    type: GOLD.type,
                    ...defaultParams
                })
            ],
            colGroups: [
                [
                    {
                        colIds: [
                            PAST_CERTIFICATES_BRONZE,
                            PAST_CERTIFICATES_SILVER,
                            PAST_CERTIFICATES_GOLD
                        ],
                        name: 'Past Certificates - Total',
                        classes: css['total-certificates'],
                        id: PAST_CERTIFICATES_TOTAL
                    }
                ]
            ]
        };
    }

    return {
        cols: [
            getCertificateDisplayColumn({
                id: PAST_CERTIFICATES_TOTAL,
                name: '',
                service: certificatesActions.pastCertificatesDownload,
                type: PAST_TOTAL.type,
                isOldCertificates: true,
                isAllYears
            })
        ],
        colGroups: [
            [
                {
                    id: PAST_CERTIFICATES_TOTAL,
                    colIds: [PAST_CERTIFICATES_TOTAL],
                    name: 'Past Certificates - Total',
                    classes: css['total-certificates']
                }
            ]
        ]
    };
};

const getAllClassesParticipationNewCertificates = (
    isExpanded,
    isAllYears,
    isTopTen
) => {
    const defaultStyles = `
        ${css['total-certificates']} 
        ${css['new-certificates']}
    `;

    if (isExpanded) {
        const defaultParams = {
            service: certificatesActions.newCertificatesDownloadByType,
            isAllYears,
            isTopTen
        };
        return {
            cols: [
                getCertificateDisplayColumn({
                    id: NEW_CERTIFICATES_BRONZE,
                    name: 'Certificates - Bronze',
                    type: BRONZE.type,
                    ...defaultParams
                }),
                getCertificateDisplayColumn({
                    id: NEW_CERTIFICATES_SILVER,
                    name: 'Certificates - Silver',
                    type: SILVER.type,
                    ...defaultParams
                }),
                getCertificateDisplayColumn({
                    id: NEW_CERTIFICATES_GOLD,
                    name: 'Certificates - Gold',
                    type: GOLD.type,
                    ...defaultParams
                }),
                getCertificateDisplayColumn({
                    id: NEW_CERTIFICATES_PLATINUM,
                    name: 'Certificates - Platinum',
                    type: PLATINUM.type,
                    ...defaultParams
                }),
                getCertificateDisplayColumn({
                    id: NEW_CERTIFICATES_EMERALD,
                    name: 'Certificates - Emerald',
                    type: EMERALD.type,
                    ...defaultParams
                }),
                getCertificateDisplayColumn({
                    id: NEW_CERTIFICATES_DIAMOND,
                    name: 'Certificates - Diamond',
                    type: DIAMOND.type,
                    ...defaultParams
                }),
                getCertificateDisplayColumn({
                    id: NEW_CERTIFICATES_EPIC,
                    name: 'Certificates - Epic',
                    type: EPIC.type,
                    ...defaultParams
                }),
                getCertificateDisplayColumn({
                    id: NEW_CERTIFICATES_LEGEND,
                    name: 'Certificates - Legend',
                    type: LEGEND.type,
                    ...defaultParams
                })
            ],
            colGroups: [
                [
                    {
                        colIds: [
                            NEW_CERTIFICATES_BRONZE,
                            NEW_CERTIFICATES_SILVER,
                            NEW_CERTIFICATES_GOLD,
                            NEW_CERTIFICATES_PLATINUM,
                            NEW_CERTIFICATES_EMERALD,
                            NEW_CERTIFICATES_DIAMOND,
                            NEW_CERTIFICATES_EPIC,
                            NEW_CERTIFICATES_LEGEND
                        ],
                        name: 'New Certificates - Total',
                        classes: isExpanded
                            ? `
                                ${defaultStyles}
                                ${css['new-expanded']}`
                            : defaultStyles,
                        id: NEW_CERTIFICATES_TOTAL
                    }
                ]
            ]
        };
    }

    return {
        cols: [
            getCertificateDisplayColumn({
                id: NEW_CERTIFICATES_TOTAL,
                name: '',
                service: certificatesActions.newCertificatesDownload,
                type: NEW_TOTAL.type,
                isAllYears,
                isTopTen
            })
        ],
        colGroups: [
            [
                {
                    id: NEW_CERTIFICATES_TOTAL,
                    colIds: [NEW_CERTIFICATES_TOTAL],
                    name: 'New Certificates - Total',
                    classes: defaultStyles
                }
            ]
        ]
    };
};

const getAllClassesParticipationTableConfig = ({
    isNewCertificatesExpanded = false,
    isPastCertificatesExpanded = false,
    isAllYears = false,
    isTopTen = false
}) => {
    const isExpanded = isNewCertificatesExpanded || isPastCertificatesExpanded;
    const pastCertificatesConfig = getAllClassesParticipationPastCertificates(
        isPastCertificatesExpanded,
        isAllYears,
        isTopTen
    );
    const newCertificatesConfig = getAllClassesParticipationNewCertificates(
        isNewCertificatesExpanded,
        isAllYears,
        isTopTen
    );

    return {
        colGroups: [
            [
                {
                    colIds: [CLASS_NAME],
                    name: 'Table Config - All classes',
                    sort: {
                        colId: CLASS_NAME,
                        sortAs: 'string'
                    },
                    classes: css['class-name']
                },
                {
                    colIds: [LIVE_POINTS],
                    name: isExpanded
                        ? 'Points - Live - Small'
                        : 'Points - Live',
                    sort: {
                        colId: LIVE_POINTS,
                        ...sort
                    },
                    classes: isExpanded
                        ? `${css['total-points']} ${css.small}`
                        : css['total-points']
                },
                {
                    colIds: [ACTIVITY_POINTS],
                    name: isExpanded
                        ? 'Points - Curriculum - Small'
                        : 'Points - Curriculum',
                    sort: {
                        colId: ACTIVITY_POINTS,
                        ...sort
                    },
                    classes: isExpanded
                        ? `${css['total-points']} ${css.small}`
                        : css['total-points']
                },
                {
                    colIds: [TOTAL_POINTS],
                    name: isExpanded
                        ? 'Points - Total - Small'
                        : 'Points - Total',
                    sort: {
                        colId: TOTAL_POINTS,
                        ...sort
                    },
                    classes: isExpanded
                        ? `${css['total-points']} ${css.small}`
                        : css['total-points']
                },
                ...newCertificatesConfig.colGroups[0],
                ...pastCertificatesConfig.colGroups[0]
            ]
        ],
        cols: [
            {
                id: CLASS_NAME,
                name: '',
                doTranslation: true,
                truncate: true,
                onClick: () => null // this is set in the AllClasses component
            },
            {
                id: LIVE_POINTS,
                name: ''
            },
            {
                id: ACTIVITY_POINTS,
                name: ''
            },
            {
                id: TOTAL_POINTS,
                name: ''
            },
            ...newCertificatesConfig.cols,
            ...pastCertificatesConfig.cols
        ],
        defaultSort: CLASS_NAME,
        emptyDataMessage: 'Empty - NoParticipationResults'
    };
};

const getAllYearsParticipationTableConfig = ({
    isNewCertificatesExpanded = false,
    isPastCertificatesExpanded = false
}) => {
    const ALL_YEARS_PARTICIPATION_TABLE_CONFIG = cloneDeep({
        ...getAllClassesParticipationTableConfig({
            isNewCertificatesExpanded,
            isPastCertificatesExpanded,
            isAllYears: true
        }),
        defaultSort: CLASS_ID
    });
    ALL_YEARS_PARTICIPATION_TABLE_CONFIG.colGroups[0][0] = {
        colIds: [CLASS_ID],
        name: 'Export - All years',
        sort: {
            colId: CLASS_ID,
            ...sort
        },
        classes: css['class-name']
    };
    ALL_YEARS_PARTICIPATION_TABLE_CONFIG.cols[0] = {
        name: '',
        id: CLASS_ID,
        transform: (row) => row[CLASS_NAME]
    };

    return ALL_YEARS_PARTICIPATION_TABLE_CONFIG;
};

const getTop10ParticipationTableConfig = ({
    isNewCertificatesExpanded = false,
    isPastCertificatesExpanded = false
}) => {
    const TOP_TEN_PARTICIPATION_TABLE_CONFIG = cloneDeep({
        ...getAllClassesParticipationTableConfig({
            isNewCertificatesExpanded,
            isPastCertificatesExpanded,
            isTopTen: true
        }),
        defaultSort: TOTAL_POINTS
    });
    TOP_TEN_PARTICIPATION_TABLE_CONFIG.colGroups[0][0] = {
        ...TOP_TEN_PARTICIPATION_TABLE_CONFIG.colGroups[0][0],
        name: 'Export - Top Students'
    };

    const yearColGroup = {
        colIds: [YEAR_GROUP],
        name: 'Label - Year',
        sort: {
            colId: YEAR_GROUP,
            ...sort
        },
        classes: `${css['year-group']} ${css.small}`
    };
    const yearCol = {
        id: YEAR_GROUP,
        name: ''
    };
    TOP_TEN_PARTICIPATION_TABLE_CONFIG.colGroups[0].splice(1, 0, yearColGroup);
    TOP_TEN_PARTICIPATION_TABLE_CONFIG.cols.splice(1, 0, yearCol);
    return TOP_TEN_PARTICIPATION_TABLE_CONFIG;
};

const ALL_CLASSES_USAGE_TABLE_CONFIG = {
    cols: [
        {
            id: SIGN_INS,
            name: 'Header - Sign-ins'
        },
        {
            id: TIME_ONLINE,
            name: 'Header - TimeOnline',
            transform: (row) => {
                const { hours, minutes } = row.totalTimeOnline;
                return `${hours}h ${minutes}min`;
            }
        },
        {
            id: TOTAL_ACTIVITY_POINTS,
            name: 'Points - Activity'
        },
        {
            id: TOTAL_SKILL_QUESTS_POINTS,
            name: 'Points - Skill'
        },
        {
            id: TOTAL_LIVE_POINTS,
            name: 'Header - LivePoints'
        }
    ]
};

export {
    getAllClassesParticipationTableConfig,
    getAllYearsParticipationTableConfig,
    getTop10ParticipationTableConfig,
    ALL_CLASSES_USAGE_TABLE_CONFIG
};



// WEBPACK FOOTER //
// ./containers/participation/all-classes/all-classes-constants.js